import { isHiddenBookmarkName, Queries } from '@aline/common';
import type { Editor, Path } from 'slate';
import { Node } from 'slate';

interface CrossReferenceTargetBase {
  key: string;
  label: string;
  level: number;
  path: Path;
}

export interface HeadingTarget extends CrossReferenceTargetBase {
  kind: 'heading';
  variant: string;
}

export interface BookmarkTarget extends CrossReferenceTargetBase {
  kind: 'bookmark';
  name: string;
}

export interface NumberedItemTarget extends CrossReferenceTargetBase {
  kind: 'numberedItem';
  markerText: string;
}

export type CrossReferenceTarget = HeadingTarget | BookmarkTarget | NumberedItemTarget;

export function scanCrossReferenceTargets(
  editor: Editor,
  referenceType: string
): CrossReferenceTarget[] {
  switch (referenceType) {
    case 'heading':
      return scanHeadingTargets(editor);
    case 'bookmark':
      return scanBookmarkTargets(editor);
    case 'numberedItem':
      return scanNumberedItemTargets(editor);
    default:
      return [];
  }
}

function scanHeadingTargets(editor: Editor): HeadingTarget[] {
  return Queries.getHeadings(editor)
    .map(([node, path]): HeadingTarget => {
      const listLevel = Queries.renderedParagraphListProps(node)?.listLevel;

      return {
        key: Queries.findKey(node).id,
        kind: 'heading',
        label: Node.string(node).trim(),
        level: listLevel ?? headingVariantLevel(node.variant),
        path,
        variant: node.variant,
      };
    })
    .filter(target => target.label !== '');
}

function scanBookmarkTargets(editor: Editor): BookmarkTarget[] {
  return Queries.getBookmarks(editor)
    .filter(([node]) => !isHiddenBookmarkName(node.name))
    .map(
      ([node, path]): BookmarkTarget => ({
        key: node.name,
        kind: 'bookmark',
        label: node.name,
        level: 0,
        name: node.name,
        path,
      })
    );
}

function scanNumberedItemTargets(editor: Editor): NumberedItemTarget[] {
  return Queries.getOrderedListItems(editor).map((entry): NumberedItemTarget => {
    const [node, path] = entry;
    const markerText = Queries.listMarker(editor, entry)?.text ?? '';
    const text = Node.string(node).trim();

    return {
      key: Queries.findKey(node).id,
      kind: 'numberedItem',
      label: text ? `${markerText} ${text}` : markerText,
      level: Queries.renderedParagraphListProps(node)?.listLevel ?? 0,
      markerText,
      path,
    };
  });
}

/**
 * Outline depth of a heading from its style variant: `style-heading1` → 0,
 * `style-heading2` → 1, and so on. Falls back to 0 when there is no trailing
 * number.
 */
function headingVariantLevel(variant: string): number {
  const digits = /(\d+)$/.exec(variant)?.[1];
  return digits ? Math.max(0, Number(digits) - 1) : 0;
}
